import { useState, useEffect } from 'react';
import { Alert, IconButton, Box } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import { MaggieHttp } from '../../../services/MaggieHttp';

interface CashFlowsAlertBannerProps {
  companyId: number;
  companyName: string;
  onExportClick: () => void;
}

interface CashFlowCounts {
  cashIn: number;
  cashOut: number;
}

interface MetricsResponse {
  metrics: Array<{
    transactions?: Array<{
      investmentAmount?: number;
      distributions?: number;
    }>;
    fmv?: number;
  }>;
}

export function CashFlowsAlertBanner({ companyId, companyName, onExportClick }: CashFlowsAlertBannerProps) {
  const [isDismissed, setIsDismissed] = useState(
    () => sessionStorage.getItem(`cashFlowBanner-${companyId}`) === 'dismissed'
  );

  const [cashFlowCounts, setCashFlowCounts] = useState<CashFlowCounts | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Negative ids are samples or "no company selected"; /metrics has nothing for them.
    if (isDismissed || companyId < 0) {
      return;
    }

    async function fetchCounts() {
      setIsLoading(true);
      try {
        const response = await MaggieHttp.maggie.get<MetricsResponse>('/metrics', {
          params: {
            companyId,
            includeTransactions: true,
          },
        });

        const metrics = response.data.metrics?.[0];
        if (!metrics) {
          return;
        }

        const transactions = metrics.transactions || [];
        const cashOutCount = transactions.filter((t) => t.investmentAmount).length;
        // FMV is counted as a cash-in event because it represents realized value
        const cashInCount = transactions.filter((t) => t.distributions).length + (metrics.fmv ? 1 : 0);

        setCashFlowCounts({ cashIn: cashInCount, cashOut: cashOutCount });
      } catch (error) {
        console.error('Failed to fetch cash flow counts:', error);
        // Silently fail - banner won't show if we can't get counts
      } finally {
        setIsLoading(false);
      }
    }

    fetchCounts();
  }, [companyId, isDismissed]);

  const handleDismiss = () => {
    sessionStorage.setItem(`cashFlowBanner-${companyId}`, 'dismissed');
    setIsDismissed(true);
  };

  if (isDismissed || !cashFlowCounts || isLoading) {
    return null;
  }

  return (
    <Alert
      severity='info'
      sx={{ mb: 2 }}
      action={
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size='small' onClick={onExportClick} aria-label='Export cash flows'>
            <DownloadIcon />
          </IconButton>
          <IconButton size='small' onClick={handleDismiss} aria-label='Dismiss banner'>
            <CloseIcon />
          </IconButton>
        </Box>
      }
    >
      {companyName} has {cashFlowCounts.cashIn} Cash In and {cashFlowCounts.cashOut} Cash Out events
    </Alert>
  );
}
